import { reverse } from 'named-urls'
import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BooleanParam, useQueryParams } from 'use-query-params'

import { Toasts } from '@fourthwall/components'
import { useCurrentShopQuery } from '@modules/Dashboard/hooks/useCurrentShopQuery'
import { routing } from '@utils/routing'

import type { FormValues } from './ShippingGroup.types'
import { ShippingGroupView } from './ShippingGroupView'
import { INITIAL_SHIPPING_COUNTRIES } from './consts'
import { useSaveShippingProfileMutation } from './queries/useSaveShippingProfileMutation'
import { useShippingProfileQuery } from './queries/useShippingProfileQuery'

export const ShippingGroupContainer = () => {
  const { currentShopQuery } = useCurrentShopQuery()
  const navigate = useNavigate()
  const { shippingGroupId = '' } = useParams<'shippingGroupId'>()
  const [queryParams] = useQueryParams({
    selfFulfilled: BooleanParam,
  })
  const isEdit = Boolean(shippingGroupId)
  const { shippingProfileQuery, invalidateShippingProfileQuery } = useShippingProfileQuery(
    [shippingGroupId],
    {
      enabled: isEdit,
    },
  )
  const { saveShippingProfileMutation } = useSaveShippingProfileMutation()
  const shippingGroup = shippingProfileQuery.data

  const navigateToEditRoute = (id: string) =>
    navigate({
      pathname: reverse(routing.settings.shipping.group.self, { shippingGroupId: id }),
      search: location.search,
    })

  const handleSubmit = async (values: FormValues) => {
    await saveShippingProfileMutation.mutateAsync(
      [
        {
          id: shippingGroupId || undefined,
          offerIds: values.selectedProductsIds,
          countries: values.selectedCountries,
        },
      ],
      {
        onSuccess: ({ id }) => {
          invalidateShippingProfileQuery()
          Toasts.notify(
            isEdit ? 'Shipping group has been updated.' : 'Shipping group has been created.',
            { type: 'success' },
          )
          if (!isEdit && id) {
            navigateToEditRoute(id)
          }
        },
      },
    )
  }

  return (
    <ShippingGroupView
      isLoading={isEdit && shippingProfileQuery.isLoading}
      isSelfFulfilled={!!queryParams.selfFulfilled}
      initialValues={{
        selectedProductsIds: isEdit && shippingGroup ? shippingGroup.offerIds : [],
        selectedCountries:
          isEdit && shippingGroup ? shippingGroup.countries : INITIAL_SHIPPING_COUNTRIES,
      }}
      shippingLocation={currentShopQuery.data?.originAddress}
      onSubmit={handleSubmit}
    />
  )
}
